import { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { MapPin, CalendarCheck, Compass, Recycle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Agent card: standing StaffAssignment + today's dated schedules.
export const MyDailyAssignments = () => {
  const [data, setData] = useState(null);
  const [optimizing, setOptimizing] = useState(null); // schedule_id being optimized

  const load = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/company/my-daily-assignments`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error('Failed to load daily assignments:', e);
    }
  };

  useEffect(() => { load(); }, []);

  const optimize = async (scheduleId) => {
    setOptimizing(scheduleId);
    // Try to seed from the agent's current GPS. Falls back to no-seed on
    // refusal/error — the backend handles either case.
    const seed = await new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { timeout: 4000, maximumAge: 60_000 },
      );
    });
    try {
      const res = await fetch(`${BACKEND_URL}/api/schedules/${scheduleId}/optimize-route`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(seed || {}),
      });
      const d = await res.json();
      if (!res.ok) {
        toast.error(d.detail || 'Could not optimize route');
        return;
      }
      if (!d.changed) {
        toast.info(d.message || 'Route already optimal.');
      } else {
        toast.success(`Route optimized · ${d.stops?.length || 0} stops${d.seeded_from_gps ? ' (seeded from your location)' : ''}`);
      }
      load();
    } catch (e) {
      toast.error('Route optimization failed');
    } finally {
      setOptimizing(null);
    }
  };

  const standing = data?.standing;
  const schedules = data?.schedules_today || [];
  const totals = data?.totals;

  return (
    <Card className="bg-zinc-900 border-2 p-6" style={{ borderColor: 'rgba(59, 130, 246, 0.5)' }} data-testid="my-daily-assignments-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-bold text-lg flex items-center text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <CalendarCheck className="h-5 w-5 mr-2 text-blue-400" />
          MY DAILY ASSIGNMENTS · {data?.date || '—'}
        </h3>
        <div className="flex items-center gap-1.5">
          {totals && (totals.units > 0 || totals.properties > 0) && (
            <Badge className="bg-emerald-700 text-white text-[11px] font-bold" data-testid="my-assignment-totals">
              TOTAL: {totals.properties} propert{totals.properties === 1 ? 'y' : 'ies'} · {totals.units} units
            </Badge>
          )}
          {standing?.region_code && (
            <Badge className="bg-blue-600 text-white text-[11px] font-bold" data-testid="my-assignment-region">
              Region {standing.region_code}
            </Badge>
          )}
        </div>
      </div>

      {!standing && schedules.length === 0 ? (
        <p className="text-zinc-400 text-sm" data-testid="my-assignments-empty">
          No assignments yet — your Supervisor or Crew Leader will assign your communities.
        </p>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          <div data-testid="my-standing-assignment">
            <p className="text-xs font-bold text-zinc-400 uppercase mb-1.5">Standing Assignment</p>
            {!standing ? (
              <p className="text-xs text-zinc-500">None assigned.</p>
            ) : (
              <div className="space-y-1.5">
                {standing.assigned_cities?.length > 0 && (
                  <p className="text-xs text-zinc-300">
                    <span className="text-zinc-500">Cities:</span> {standing.assigned_cities.join(', ')}
                  </p>
                )}
                {(standing.properties || []).map((p) => (
                  <div key={p.property_id} className="flex items-start gap-1.5 bg-zinc-800 border border-zinc-700 rounded-md px-2.5 py-1.5" data-testid={`my-assigned-property-${p.property_id}`}>
                    <MapPin className="h-3.5 w-3.5 text-blue-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-white">{p.property_name}</p>
                      <p className="text-[11px] text-zinc-400">
                        {p.address}{p.city ? ` · ${p.city}` : ''}
                        {p.total_units ? ` · ${p.total_units} units` : ''}
                        {p.total_buildings ? ` · ${p.total_buildings} bldgs` : ''}
                      </p>
                    </div>
                  </div>
                ))}
                <p className="text-[10px] text-zinc-500">Assigned by {standing.assigned_by_name || '—'}</p>
              </div>
            )}
          </div>

          <div data-testid="my-todays-schedules">
            <p className="text-xs font-bold text-zinc-400 uppercase mb-1.5">Today&rsquo;s Job Card</p>
            {schedules.length === 0 ? (
              <p className="text-xs text-zinc-500">No dated schedule for today.</p>
            ) : schedules.map((s, i) => {
              const buildings = s.buildings || [];
              const unitTotal = buildings.reduce((n, b) => n + (b.units || []).length, 0);
              return (
                <div key={s.schedule_id || i} className="bg-zinc-800 border border-zinc-700 rounded-md px-2.5 py-2 mb-1.5" data-testid={`my-schedule-${i}`} data-property-id={s.property_id || (s.properties || [])[0]?.property_id || ''}>
                  <div className="flex items-center justify-between" data-testid={s.property_id ? `my-assigned-property-${s.property_id}` : undefined}>
                    <p className="text-xs font-bold text-white">
                      {s.property_name || (s.properties || [])[0]?.property_name || 'Assigned route'}
                      {s.total_units ? <span className="text-zinc-400 font-normal ml-1">· {s.total_units} units total</span> : null}
                    </p>
                    <Badge className="bg-zinc-700 text-zinc-200 text-[10px]">{s.status || 'scheduled'}</Badge>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mt-1">
                    <Badge className="bg-blue-600/30 border border-blue-500 text-blue-300 text-[10px]" data-testid={`my-schedule-units-${i}`}>
                      {unitTotal > 0 ? `${unitTotal} units assigned` : 'Whole property'}
                    </Badge>
                    {buildings.map((b) => (
                      <Badge key={b.building_number} className="bg-zinc-700 text-zinc-200 text-[10px]" data-testid={`my-schedule-bldg-${i}-${b.building_number}`}>
                        Bldg {b.building_number} · {(b.units || []).length}
                      </Badge>
                    ))}
                  </div>
                  {buildings.some((b) => (b.units || []).length > 0) && (
                    <p className="text-[10px] text-zinc-400 font-mono mt-1">
                      {buildings.map((b) => `B${b.building_number}: ${(b.units || []).join(', ')}`).join(' · ')}
                    </p>
                  )}
                  <p className="text-[10px] text-zinc-500 mt-1">
                    {(s.shift_type || 'shift').replace('_', ' ').toUpperCase()}
                    {s.delegated_by_name ? ` · delegated by ${s.delegated_by_name}` : ''}
                  </p>
                  {(s.properties || []).length > 0 && (
                    <div className="mt-1.5 border-t border-zinc-700/50 pt-1.5" data-testid={`my-schedule-route-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">Execution order</p>
                        {(s.properties || []).length > 1 && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={optimizing === s.schedule_id}
                            onClick={() => optimize(s.schedule_id)}
                            className="h-6 px-2 text-[10px] border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
                            data-testid={`optimize-route-btn-${i}`}
                          >
                            <Compass className="h-3 w-3 mr-1" />
                            {optimizing === s.schedule_id ? 'Optimizing…' : 'Optimize my route'}
                          </Button>
                        )}
                      </div>
                      <ol className="space-y-0.5">
                        {(s.properties || []).map((p, idx) => (
                          <li
                            key={p.property_id}
                            className="text-[11px] text-zinc-300 flex items-start gap-2"
                            data-testid={`my-schedule-route-stop-${i}-${idx + 1}`}
                          >
                            <span className="inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-300 text-[9px] font-bold">
                              {p.stop_number || idx + 1}
                            </span>
                            <span>
                              <b>{p.property_name}</b>
                              {p.city ? <span className="text-zinc-500"> · {p.city}</span> : null}
                              {p.total_units ? <span className="text-zinc-500"> · {p.total_units} units</span> : null}
                              {p.total_buildings ? <span className="text-zinc-500"> · {p.total_buildings} bldgs</span> : null}
                              {p.has_recycling_service && (
                                <Badge
                                  className="ml-1.5 bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-[9px] px-1 py-0"
                                  data-testid={`recycling-badge-${p.property_id}`}
                                >
                                  <Recycle className="h-2.5 w-2.5 mr-0.5" /> Recycling
                                </Badge>
                              )}
                            </span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
};
